import { createSlice, createAction } from "@reduxjs/toolkit";
import _set from "lodash/set";
import _get from "lodash/get";

import {
  LOADING_STATE,
  ERROR_STATE,
  SUCCESS_STATE,
} from "../constants/operationsState";

import { APPLICATION_ENV, ENV_SANDBOX } from "../constants/environment";

import { generateUniqueId } from "../utils/unique-id";
import { RootState } from "./index";
import { UUID } from "../utils";

type OperationState =
  | { type: typeof LOADING_STATE }
  | { type: typeof ERROR_STATE; error?: any }
  | { type: typeof SUCCESS_STATE };

type SliceState = {
  snackbars: {
    key: string | number;
    i18nMessage: string;
    options?: object;
  }[];
  operationsState: Record<string, OperationState | null>;
  environment: APPLICATION_ENV;
  impersonatedCustomer: { id: UUID; name: string; allIds: UUID[] } | null;
};

export const initialState: SliceState = {
  snackbars: [],
  operationsState: {},
  environment: ENV_SANDBOX,
  impersonatedCustomer: null,
};

const enqueueSnackbarReducer = (
  state: SliceState,
  {
    payload,
  }: {
    payload: { key?: string | number; i18nMessage: string; options?: object };
  }
) => {
  const { key = generateUniqueId(), ...snackbar } = payload;
  state.snackbars.push({
    key,
    ...snackbar,
  });
};

export const slice = createSlice({
  name: "app",
  initialState,
  reducers: {
    startLoading: (state, action) => {
      _set(state.operationsState, action.payload, { type: LOADING_STATE });
    },
    setError(state, { payload }) {
      const { path, error } = payload;
      _set(state.operationsState, path, { type: ERROR_STATE, error });
    },
    setSuccess(state, action) {
      _set(state.operationsState, action.payload, { type: SUCCESS_STATE });
    },
    clearOperation(state, action) {
      _set(state.operationsState, action.payload, null);
    },
    clearResult(state, action) {
      const path = action.payload;
      const operationState = _get(state.operationsState, path);
      if (
        operationState &&
        [ERROR_STATE, SUCCESS_STATE].includes(operationState.type)
      ) {
        _set(state.operationsState, path, null);
      }
    },
    enqueueSnackbar: enqueueSnackbarReducer,
    closeSnackbar: (state, { payload }) => {
      const { dismissAll, key } = payload;
      state.snackbars = state.snackbars.map((snackbar) =>
        dismissAll || snackbar.key === key
          ? { ...snackbar, dismissed: true }
          : { ...snackbar }
      );
    },
    removeSnackbar: (state, { payload: key }) => {
      state.snackbars = state.snackbars.filter(
        (snackbar) => snackbar.key !== key
      );
    },
    enqueueSnackbarError(state, { payload }: { payload: { code: string } }) {
      const { code } = payload;
      enqueueSnackbarReducer(state, {
        payload: {
          i18nMessage: code,
          options: {
            variant: "error",
          },
        },
      });
    },
    enqueueSnackbarSuccess(state, { payload }: { payload: { code: string } }) {
      const { code } = payload;
      enqueueSnackbarReducer(state, {
        payload: {
          i18nMessage: code,
          options: {
            variant: "success",
          },
        },
      });
    },
    setEnvironment: (state, action) => {
      state.environment = action.payload;
    },
    setImpersonatedCustomer: (
      state,
      { payload }: { payload: { id: UUID; name: string; allIds: UUID[] } }
    ) => {
      state.impersonatedCustomer = payload;
    },
    clearImpersonatedCustomer: (state) => {
      state.impersonatedCustomer = null;
    },
  },
});

export const {
  startLoading,
  setError,
  setSuccess,
  clearOperation,
  clearResult,
  enqueueSnackbar,
  enqueueSnackbarError,
  enqueueSnackbarSuccess,
  closeSnackbar,
  removeSnackbar,
  setEnvironment,
  setImpersonatedCustomer,
  clearImpersonatedCustomer,
} = slice.actions;

export const init = createAction("init");
export const toggleEnvironment = createAction("toggleEnvironment");

export const selectEnvironment = (state: RootState) => state.app.environment;

export const selectOperationState = (path: string) => (state: RootState) =>
  _get(state.app.operationsState, path);

export const selectImpersonatedCustomer = (state: RootState) =>
  state.app.impersonatedCustomer;

export default slice.reducer;
